import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, Music2, Cookie } from "lucide-react";
import { openCookieSettings } from "@/lib/consent";
import { PinterestIcon, ThreadsIcon } from "@/components/icons/Social";
import { MEDIA } from "@/lib/media";
import { useCollectionCounts, isLiveLink } from "@/lib/collections";
import { Paw } from "@/components/decor";
import { useSocial } from "@/lib/social";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const cols = [
  { title: "Shop", links: [
    ["Shop All", "/shop"], ["Books", "/shop?collection=books"], ["Clothing", "/shop?collection=unisex"],
    ["Stickers & Transfers", "/shop?collection=stickers-transfers"], ["Accessories", "/shop?collection=accessories"],
    ["Dog Collection", "/shop?collection=dogs"], ["Gift Ideas", "/shop?collection=gifts"], ["Gift Cards", "/gift-cards"], ["Check Gift Balance", "/gift-cards/balance"]] },
  { title: "Help", links: [
    ["Contact Us", "/contact"], ["FAQ", "/faq"], ["Shipping", "/legal/shipping-policy"],
    ["Returns", "/legal/refund-policy"], ["Size Guide", "/size-guide"], ["Free Bulldog Guide", "/bulldog-guide"], ["Pup of the Month", "/pup-of-the-month"], ["Pawparazzi Red Carpet", "/pawparazzi-red-carpet"], ["Pup of the Wall", "/pup-of-the-wall"], ["Favourite Places Map", "/places"], ["Pup Birthday Club", "/pup-birthday-club"], ["Where's my order?", "/track"]] },
  { title: "About", links: [
    ["Our Story", "/our-story"], ["Meet Chewie & Solo", "/meet"], ["Meet the Author", "/our-story#author"],
    ["Chewie & Solo Adventures", "/adventures"], ["Blog / Adventures", "/adventures"]] },
  { title: "Legal", links: [
    ["Terms & Conditions", "/legal/terms-conditions"], ["Privacy Policy", "/legal/privacy-policy"], ["Cookie Policy", "/legal/cookie-policy"],
    ["Refund Policy", "/legal/refund-policy"], ["Shipping Policy", "/legal/shipping-policy"],
    ["Copyright", "/legal/copyright"], ["Accessibility", "/legal/accessibility"]] },
];

export default function Footer() {
  const social = useSocial();
  const counts = useCollectionCounts();
  const socials = [["instagram", Instagram], ["facebook", Facebook], ["pinterest", PinterestIcon], ["threads", ThreadsIcon], ["tiktok", Music2]].filter(([k]) => social[k]);
  return (
    <footer className="relative mt-4 bg-[#19324A] text-white overflow-hidden" data-testid="site-footer">
      <div className="absolute inset-0 opacity-[0.05] pointer-events-none">
        {[...Array(8)].map((_, i) => (
          <Paw key={i} className="absolute w-24 h-24 text-white" style={{ top: `${(i * 29) % 90}%`, left: `${(i * 41) % 90}%`, transform: `rotate(${i * 40}deg)` }} />
        ))}
      </div>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-14">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 lg:gap-12">
          {cols.map((c) => (
            <div key={c.title}>
              <h4 className="font-display font-bold text-[#FFD93D] mb-4 text-lg">{c.title}</h4>
              <ul className="space-y-2.5">
                {c.links.filter(([, to]) => isLiveLink(to, counts)).map(([label, to]) => (
                  <li key={label}><Link to={to} className="inline-flex items-center min-h-[40px] text-white/75 hover:text-white text-sm transition-colors">{label}</Link></li>
                ))}
                {c.title === "Legal" && (
                  <li><button type="button" onClick={openCookieSettings} className="inline-flex items-center gap-1.5 min-h-[40px] text-white/75 hover:text-white text-sm transition-colors" data-testid="footer-cookie-settings">
                    <Cookie className="w-4 h-4" /> Cookie Settings</button></li>
                )}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-12 pt-8 border-t border-white/15 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <img src={MEDIA.logoStacked} alt="The Happy Bulldog Co." className="h-20 w-auto object-contain" />
          </div>
          <div className="flex items-center gap-3">
            {socials.map(([k, Icon]) => (
              <a key={k} href={social[k]} target="_blank" rel="noopener noreferrer" aria-label={k}
                className="w-11 h-11 rounded-full bg-white/10 hover:bg-[#FF6B9D] flex items-center justify-center transition-colors" data-testid={`footer-social-${k}`}>
                <Icon className="w-5 h-5" />
              </a>
            ))}
            {socials.length === 0 && <span className="text-white/50 text-sm" data-testid="footer-social-soon">Socials coming soon 🐾</span>}
          </div>
        </div>
        <p className="mt-8 text-center text-white/60 text-sm">
          © {new Date().getFullYear()} The Happy Bulldog Co.™ — Big Personalities. Little Paws. Endless Adventures.
        </p>
      </div>
    </footer>
  );
}
